import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Divider,
  IconButton,
  TextField,
} from "@mui/material";
import {
  ShowChart,
  Description,
  Visibility,
  FileDownload,
  Delete,
} from "@mui/icons-material";
import { samplePMCals, sampleAssets } from "./pmCalData";

// ─── Report types from HTML source ───────────────────────────────────────────
const REPORT_TYPES = [
  { value: "monthly", label: "Monthly Report" },
  { value: "compliance", label: "Compliance Report" },
  { value: "yearly", label: "Yearly Summary" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const generateMonthlyData = (startDate, endDate) => {
  const rangeStart = startDate ? new Date(startDate) : new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const rangeEnd = endDate ? new Date(endDate) : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
  rangeEnd.setHours(23, 59, 59);

  const filtered = samplePMCals.filter((p) => {
    const d = new Date(p.startDate);
    return d >= rangeStart && d <= rangeEnd;
  });

  return {
    totalGenerated: filtered.length,
    completed: filtered.filter((p) => p.status === "completed").length,
    pending: filtered.filter((p) => p.status === "pending").length,
    inProgress: filtered.filter((p) => p.status === "in-progress").length,
    overdue: filtered.filter((p) => {
      const due = new Date(p.dueDate);
      return due < new Date() && p.status !== "completed";
    }).length,
    pmCals: filtered,
  };
};

const generateComplianceData = () => {
  const assetCompliance = sampleAssets.map((asset) => {
    const assetPMCals = samplePMCals.filter((p) => p.assetId === asset.id);
    const completed = assetPMCals.filter((p) => p.status === "completed").length;
    return {
      assetId: asset.id,
      assetName: asset.name,
      totalPMCals: assetPMCals.length,
      completedPMCals: completed,
      complianceRate: assetPMCals.length > 0 ? Math.round((completed / assetPMCals.length) * 100) : 0,
      status: asset.calibrationStatus || "unknown",
    };
  });
  return {
    totalAssets: sampleAssets.length,
    assetsInCompliance: assetCompliance.filter((a) => a.complianceRate >= 90).length,
    overallComplianceRate:
      assetCompliance.length > 0
        ? Math.round(assetCompliance.reduce((s, a) => s + a.complianceRate, 0) / assetCompliance.length)
        : 0,
    assetCompliance,
  };
};

const generateYearlyData = (startDate, endDate) => {
  const year = startDate ? new Date(startDate).getFullYear() : new Date().getFullYear();
  const rangeStart = startDate ? new Date(startDate) : new Date(year, 0, 1);
  const rangeEnd = endDate ? new Date(endDate) : new Date(year, 11, 31, 23, 59, 59);

  const filtered = samplePMCals.filter((p) => {
    const d = new Date(p.startDate);
    return d >= rangeStart && d <= rangeEnd;
  });

  return {
    totalGenerated: filtered.length,
    completed: filtered.filter((p) => p.status === "completed").length,
    pending: filtered.filter((p) => p.status === "pending").length,
    inProgress: filtered.filter((p) => p.status === "in-progress").length,
    overdue: filtered.filter((p) => {
      const due = new Date(p.dueDate);
      return due < new Date() && p.status !== "completed";
    }).length,
    pmCals: filtered,
  };
};

// ─── View Report in new window (from additional-features.js) ─────────────────
const viewReportInWindow = (report) => {
  const typeLabels = { monthly: "Monthly Report", compliance: "Compliance Report", yearly: "Yearly Summary" };
  const reportTitle = typeLabels[report.type] || "Report";
  const generatedAt = new Date(report.generatedAt).toLocaleString();

  let summaryHTML = "";
  let detailHTML = "";

  if (report.type === "monthly" || report.type === "yearly") {
    const d = report.data;
    const total = d.totalGenerated || 0;
    const compPct = total > 0 ? Math.round((d.completed / total) * 100) : 0;

    summaryHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:14px;margin-bottom:8px;">
        <div style="background:#eff6ff;border-left:4px solid #3b82f6;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${total}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Total PM-Cals</div></div>
        <div style="background:#f0fdf4;border-left:4px solid #22c55e;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.completed}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Completed</div></div>
        <div style="background:#fff7ed;border-left:4px solid #f97316;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.pending}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Pending</div></div>
        <div style="background:#faf5ff;border-left:4px solid #a855f7;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.inProgress}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">In Progress</div></div>
        <div style="background:#fef2f2;border-left:4px solid #ef4444;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.overdue}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Overdue</div></div>
        <div style="background:#f0fdfa;border-left:4px solid #14b8a6;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${compPct}%</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Completion Rate</div></div>
      </div>`;

    if (d.pmCals && d.pmCals.length > 0) {
      const statusBadge = (s) => {
        const map = { completed: "#dcfce7;color:#15803d", pending: "#fed7aa;color:#c2410c", "in-progress": "#ede9fe;color:#7c3aed" };
        const style = map[s] || "#f1f5f9;color:#475569";
        return `<span style="display:inline-block;padding:3px 9px;border-radius:99px;font-size:10px;font-weight:700;background:${style}">${s}</span>`;
      };
      const rows = d.pmCals
        .map(
          (p, i) => `<tr style="background:${i % 2 === 0 ? "#f8fafc" : "#fff"}">
        <td style="padding:9px 12px">${p.id}</td>
        <td style="padding:9px 12px">${p.assetName}</td>
        <td style="padding:9px 12px">${p.toolName || "—"}</td>
        <td style="padding:9px 12px">${p.startDate}</td>
        <td style="padding:9px 12px">${p.dueDate}</td>
        <td style="padding:9px 12px">${p.frequency || "—"}</td>
        <td style="padding:9px 12px">${statusBadge(p.status)}</td>
      </tr>`
        )
        .join("");
      detailHTML = `<h3 style="font-size:14px;font-weight:700;color:#2362B4;margin:28px 0 16px;padding-bottom:8px;border-bottom:2px solid #dbeafe;">PM-Cal Detail</h3>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#2362B4;color:#fff;"><th style="padding:10px 12px;text-align:left;">PM-Cal ID</th><th style="padding:10px 12px;text-align:left;">Asset</th><th style="padding:10px 12px;text-align:left;">Tool</th><th style="padding:10px 12px;text-align:left;">Start Date</th><th style="padding:10px 12px;text-align:left;">Due Date</th><th style="padding:10px 12px;text-align:left;">Frequency</th><th style="padding:10px 12px;text-align:left;">Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    }
  } else if (report.type === "compliance") {
    const d = report.data;
    const rate = d.overallComplianceRate || 0;
    const rateColor = rate >= 90 ? "#16a34a" : rate >= 70 ? "#d97706" : "#dc2626";

    summaryHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:14px;margin-bottom:8px;">
        <div style="background:#eff6ff;border-left:4px solid #3b82f6;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.totalAssets}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Total Assets</div></div>
        <div style="background:#f0fdf4;border-left:4px solid #22c55e;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.assetsInCompliance}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">In Compliance</div></div>
        <div style="background:#fef2f2;border-left:4px solid #ef4444;border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;">${d.totalAssets - d.assetsInCompliance}</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Below Threshold</div></div>
        <div style="background:#f0fdf4;border-left:4px solid ${rateColor};border-radius:10px;padding:16px 14px;text-align:center;"><div style="font-size:28px;font-weight:800;color:${rateColor}">${rate}%</div><div style="font-size:11px;color:#64748b;margin-top:5px;">Overall Rate</div></div>
      </div>
      <div style="margin:20px 0 8px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:6px;"><span style="font-weight:600;">Compliance Rate</span><span style="font-weight:700;color:${rateColor}">${rate}%</span></div>
        <div style="background:#e2e8f0;border-radius:999px;height:12px;overflow:hidden;"><div style="height:100%;border-radius:999px;width:${rate}%;background:${rateColor};"></div></div>
      </div>`;

    if (d.assetCompliance && d.assetCompliance.length > 0) {
      const rows = d.assetCompliance
        .map((a, i) => {
          const rc = a.complianceRate >= 90 ? "#16a34a" : a.complianceRate >= 70 ? "#d97706" : "#dc2626";
          return `<tr style="background:${i % 2 === 0 ? "#f8fafc" : "#fff"}">
          <td style="padding:9px 12px">${a.assetId}</td>
          <td style="padding:9px 12px">${a.assetName}</td>
          <td style="padding:9px 12px">${a.totalPMCals}</td>
          <td style="padding:9px 12px">${a.completedPMCals}</td>
          <td style="padding:9px 12px;font-weight:700;color:${rc}">${a.complianceRate}%</td>
          <td style="padding:9px 12px"><span style="display:inline-block;padding:3px 9px;border-radius:99px;font-size:10px;font-weight:700;background:#f1f5f9;color:#475569">${a.status}</span></td>
        </tr>`;
        })
        .join("");
      detailHTML = `<h3 style="font-size:14px;font-weight:700;color:#2362B4;margin:28px 0 16px;padding-bottom:8px;border-bottom:2px solid #dbeafe;">Asset Compliance Detail</h3>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#2362B4;color:#fff;"><th style="padding:10px 12px;text-align:left;">Asset ID</th><th style="padding:10px 12px;text-align:left;">Asset Name</th><th style="padding:10px 12px;text-align:left;">Total PM-Cals</th><th style="padding:10px 12px;text-align:left;">Completed</th><th style="padding:10px 12px;text-align:left;">Compliance %</th><th style="padding:10px 12px;text-align:left;">Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    }
  }

  const win = window.open("", "_blank", "width=1000,height=800");
  if (!win) return;
  win.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>${reportTitle}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;background:#f1f5f9;color:#1e293b;font-size:13px}
  .toolbar{position:fixed;top:0;left:0;right:0;z-index:99;background:#2362B4;color:#fff;display:flex;align-items:center;justify-content:space-between;padding:10px 24px;box-shadow:0 2px 8px rgba(0,0,0,.25)}
  .toolbar-title{font-size:15px;font-weight:700}
  .btn-toolbar{display:inline-flex;align-items:center;gap:6px;padding:7px 18px;border-radius:6px;border:none;font-size:13px;font-weight:600;cursor:pointer}
  .btn-print{background:#fff;color:#2362B4}.btn-close-btn{background:rgba(255,255,255,.15);color:#fff}
  .page-wrap{padding:80px 24px 48px;max-width:960px;margin:0 auto}
  .pdf-page{background:#fff;border-radius:12px;box-shadow:0 4px 24px rgba(0,0,0,.10);overflow:hidden}
  .report-header{background:linear-gradient(135deg,#2362B4 0%,#1B4F96 60%,#3b82f6 100%);color:#fff;padding:36px 40px 28px;display:flex;justify-content:space-between}
  .report-header h1{font-size:24px;font-weight:800}.report-header .subtitle{font-size:13px;opacity:.8}
  .report-header .company{font-size:18px;font-weight:700}.report-header .tagline{font-size:11px;opacity:.7}
  .header-divider{height:5px;background:linear-gradient(90deg,#f59e0b,#ef4444,#8b5cf6,#06b6d4)}
  .meta-bar{display:flex;background:#f8fafc;border-bottom:1px solid #e2e8f0}
  .meta-item{flex:1;padding:14px 20px;border-right:1px solid #e2e8f0}
  .meta-item:last-child{border-right:none}
  .meta-label{font-size:10px;font-weight:600;text-transform:uppercase;color:#64748b;letter-spacing:.8px;margin-bottom:3px}
  .meta-value{font-size:13px;font-weight:600;color:#1e293b}
  .report-body{padding:32px 40px}
  .section-title{font-size:14px;font-weight:700;color:#2362B4;text-transform:uppercase;letter-spacing:.8px;margin:0 0 16px;padding-bottom:8px;border-bottom:2px solid #dbeafe}
  .report-footer{background:#f8fafc;border-top:1px solid #e2e8f0;padding:18px 40px;display:flex;justify-content:space-between;font-size:11px;color:#94a3b8}
  @media print{.toolbar{display:none!important}body{background:#fff}.page-wrap{padding:0;max-width:100%}.pdf-page{box-shadow:none;border-radius:0}@page{margin:10mm 12mm;size:A4 portrait}}
</style></head><body>
<div class="toolbar"><span class="toolbar-title">${reportTitle}</span><div style="display:flex;gap:10px"><button class="btn-toolbar btn-print" onclick="window.print()">Save as PDF / Print</button><button class="btn-toolbar btn-close-btn" onclick="window.close()">Close</button></div></div>
<div class="page-wrap"><div class="pdf-page">
  <div class="report-header"><div class="left"><h1>${reportTitle}</h1><div class="subtitle">Generated on ${generatedAt}</div></div><div class="right" style="text-align:right"><div class="company">Oriens EAM</div><div class="tagline">Calibration Management System</div></div></div>
  <div class="header-divider"></div>
  <div class="meta-bar"><div class="meta-item"><div class="meta-label">Report ID</div><div class="meta-value" style="font-size:11px;word-break:break-all">${report.id}</div></div><div class="meta-item"><div class="meta-label">Report Type</div><div class="meta-value">${reportTitle}</div></div><div class="meta-item"><div class="meta-label">Generated</div><div class="meta-value">${generatedAt}</div></div></div>
  <div class="report-body"><h3 class="section-title">Summary</h3>${summaryHTML}${detailHTML}</div>
  <div class="report-footer"><div>Oriens EAM — Calibration Management System — Confidential</div><div style="text-align:right">Report ID: ${report.id}</div></div>
</div></div></body></html>`);
  win.document.close();
};

// ─── CSV Export ───────────────────────────────────────────────────────────────
const exportCSV = (report) => {
  let csvContent = "";

  if ((report.type === "monthly" || report.type === "yearly") && report.data.pmCals) {
    csvContent = "PM-Cal ID,Asset,Tool,Start Date,Due Date,Status,Frequency\n";
    report.data.pmCals.forEach((p) => {
      csvContent += `"${p.id}","${p.assetName}","${p.toolName || ""}","${p.startDate}","${p.dueDate}","${p.status}","${p.frequency || ""}"\n`;
    });
  } else if (report.type === "compliance" && report.data.assetCompliance) {
    csvContent = "Asset ID,Asset Name,Total PM-Cals,Completed PM-Cals,Compliance Rate,Status\n";
    report.data.assetCompliance.forEach((a) => {
      csvContent += `"${a.assetId}","${a.assetName}","${a.totalPMCals}","${a.completedPMCals}","${a.complianceRate}%","${a.status}"\n`;
    });
  }

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${report.id}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Component ────────────────────────────────────────────────────────────────
const ReportsPM = () => {
  const dispatch = useDispatch();
  const reports = useSelector((state) => state.calibration?.generatedReports || []);
  const [reportType, setReportType] = useState("monthly");
  const [startDate, setStartDate] = useState("2026-04-01");
  const [endDate, setEndDate] = useState("2026-04-30");

  const handleGenerate = () => {
    let data;
    switch (reportType) {
      case "monthly":
        data = generateMonthlyData(startDate, endDate);
        break;
      case "compliance":
        data = generateComplianceData();
        break;
      case "yearly":
        data = generateYearlyData(startDate, endDate);
        break;
      default:
        return;
    }

    const typeLabel = REPORT_TYPES.find((t) => t.value === reportType)?.label || reportType;
    const newReport = {
      id: `${reportType.toUpperCase()}-${Date.now()}`,
      type: reportType,
      typeLabel,
      generatedAt: new Date().toISOString(),
      data,
    };
    dispatch({ type: "addCalReport", payload: newReport });
  };

  const handleDelete = (id) => {
    dispatch({ type: "deleteCalReport", payload: id });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ── Page Header ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e0e4ea", px: 3.5, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <ShowChart sx={{ color: "#2362B4", fontSize: 30 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#2362B4" }}>
            Reports
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.3, ml: "40px" }}>
          Generate and view PM-Cal reports
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, display: "flex", gap: 2.5, alignItems: "flex-start" }}>
        {/* ── Left: Generate Panel ── */}
        <Paper
          elevation={0}
          sx={{ width: 320, border: "1px solid #e0e4ea", borderRadius: 2, p: 2.5, flexShrink: 0 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1a2333", mb: 2.5 }}>
            Generate Reports
          </Typography>

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
            Report Type
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <Select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              sx={{ borderRadius: 1.5, fontSize: 14 }}
            >
              {REPORT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
            Start Date
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
            End Date
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />

          <Button
            variant="contained"
            fullWidth
            startIcon={<Description />}
            onClick={handleGenerate}
            sx={{
              bgcolor: "#2362B4",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              py: 1.1,
              "&:hover": { bgcolor: "#1B4F96" },
            }}
          >
            Generate Report
          </Button>
        </Paper>

        {/* ── Right: Generated Reports List ── */}
        <Paper elevation={0} sx={{ flex: 1, border: "1px solid #e0e4ea", borderRadius: 2, p: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1a2333", mb: 2 }}>
            Generated Reports
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {reports.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: "#9ca3af", textAlign: "center", mt: 4 }}>
              No reports generated yet. Select a report type and click Generate Report.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[...reports].reverse().map((report) => (
                <Paper
                  key={report.id}
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e4ea",
                    borderRadius: 1.5,
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Description sx={{ color: "#9ca3af", fontSize: 22 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1a2333" }}>
                        {report.typeLabel}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                        Generated on {new Date(report.generatedAt).toLocaleString()} • ID: {report.id}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      size="small"
                      onClick={() => viewReportInWindow(report)}
                      sx={{
                        bgcolor: "#2362B4",
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 1.5,
                        "&:hover": { bgcolor: "#1B4F96" },
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<FileDownload />}
                      size="small"
                      onClick={() => exportCSV(report)}
                      sx={{ textTransform: "none", color: "#4b5563", fontWeight: 500 }}
                    >
                      Export CSV
                    </Button>
                    <IconButton size="small" onClick={() => handleDelete(report.id)} sx={{ color: "#C62828" }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ReportsPM;
